import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import DemoCTA from '../ui/DemoCTA';
import './Pricing.css';

const Pricing = () => {
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // State to track billing period
  const [isAnnual, setIsAnnual] = useState(false);

  // State to track visitors (for slider)
  const [visitors, setVisitors] = useState(1000);

  // State to track recommended plan
  const [recommendedPlan, setRecommendedPlan] = useState('base');

  // Define visitor thresholds for each plan
  const visitorThresholds = {
    intro: 100,
    base: 1000,
    popular: 5000,
    enterprise: 10000,
  };

  // Price calculations with discount for annual plans
  const prices = {
    intro: {
      monthly: 0,
      annual: 0, // Free forever
    },
    base: {
      monthly: 99,
      annual: Math.round(99 * 12 * 0.9), // 10% discount on annual
    },
    popular: {
      monthly: 199,
      annual: Math.round(199 * 12 * 0.9),
    },
    enterprise: {
      monthly: 299,
      annual: Math.round(299 * 12 * 0.9),
    },
  };

  // Toggle billing period
  const toggleBillingPeriod = () => {
    setIsAnnual(!isAnnual);
  };

  // Get current price based on billing period
  const getPrice = (plan) => {
    const price = prices[plan][isAnnual ? 'annual' : 'monthly'];
    // Format the price with commas for thousands
    return price.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  };

  // Get billing text
  const getBillingText = () => {
    return isAnnual ? 'year' : 'month';
  };

  // Handle slider change
  const handleSliderChange = (e) => {
    const visitorCount = parseInt(e.target.value);
    setVisitors(visitorCount);

    // Update recommended plan based on visitor count
    if (visitorCount <= visitorThresholds.intro) {
      setRecommendedPlan('intro');
    } else if (visitorCount <= visitorThresholds.base) {
      setRecommendedPlan('base');
    } else if (visitorCount <= visitorThresholds.popular) {
      setRecommendedPlan('popular');
    } else {
      setRecommendedPlan('enterprise');
    }
  };

  // Format visitor count with commas
  const formatVisitors = (count) => {
    return count.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  };

  // Get active plan label class
  const getPlanLabelClass = (plan) => {
    return recommendedPlan === plan ? 'fw-bold text-primary' : 'text-muted';
  };

  return (
    <>
      <Helmet>
        <title>AI Chat Assist Pricing | Flexible Chatbot Plans for All</title>
        <meta
          name="description"
          content="Discover AI driven Chatbot solution with flexible subscriptions and affordable pricing plans designed to scale with your business."
        />
        <meta
          name="keywords"
          content="Pricing Plans, AI Chat Assist, Flexible Chatbot Subscriptions, ai customer service, ai service, AI Insights"
        />
        <link rel="canonical" href="https://www.aichatassist.com/pricing" />
        <meta
          property="og:title"
          content="Pricing Plans | AI Chat Assist – Smart Chatbot Subscriptions"
        />
        <meta
          property="og:description"
          content="Discover AI Chat Assist’s flexible chatbot subscriptions. Affordable pricing plans designed to scale with your business in Singapore, UK & UAE."
        />
        <meta property="og:url" content="https://www.aichatassist.com/pricing" />
        <meta property="og:type" content="website" />
        <meta
          property="og:image"
          content="https://admin.aichatassist.com/uploads/Wordpress_Plugin_Banner_6964048f39.jpg"
        />
        <meta property="og:site_name" content="AI Chat Assist" />
        <meta property="og:locale" content="en_US" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Pricing Plans | AI Chat Assist – Smart Chatbot Subscriptions"
        />
        <meta
          name="twitter:description"
          content="Discover AI Chat Assist’s flexible chatbot subscriptions. Affordable pricing plans designed to scale with your business in Singapore, UK & UAE."
        />
        <meta
          name="twitter:image"
          content="https://admin.aichatassist.com/uploads/Wordpress_Plugin_Banner_6964048f39.jpg"
        />
        <meta name="twitter:site" content="@aichatassist" />
      </Helmet>

      <div className="pricing-page py-5">
        <div className="container">
          {/* Pricing Header */}
          <div className="text-center mb-4 mt-5">
            <h1 className="fw-bold mb-3">
              Simple, Transparent <span className="hero-highlight">Pricing</span>
            </h1>
            <p className="text-muted mb-5">No Contracts. No Surprise Fees.</p>
          </div>

          {/* Visitor Slider */}
          <div className="slider-container mb-5 " style={{ marginTop: '5rem' }}>
            <div className="text-center mb-3">
              <h5 className="fw-bold">
                How many visitors do you expect per month for your website?
              </h5>
              <div className="visitor-badge position-relative mb-2">
                <span className="badge badge-main px-4 py-2 rounded-pill fs-5">
                  {formatVisitors(visitors)} Visitors
                </span>
              </div>
            </div>
            <input
              type="range"
              className="form-range w-100"
              min="0"
              max={visitorThresholds.enterprise}
              value={visitors}
              onChange={handleSliderChange}
            />
            <div className="d-flex justify-content-between mt-2">
              <div className={`text-center ${getPlanLabelClass('intro')}`} style={{ width: '25%' }}>
                <div className="border-end border-2" style={{ height: '10px' }}></div>
                <span className="d-block mt-1">Intro</span>
                <small>0-{formatVisitors(visitorThresholds.intro)}</small>
              </div>
              <div className={`text-center ${getPlanLabelClass('base')}`} style={{ width: '25%' }}>
                <div className="border-end border-2" style={{ height: '10px' }}></div>
                <span className="d-block mt-1">Base</span>
                <small>
                  {formatVisitors(visitorThresholds.intro + 1)}-
                  {formatVisitors(visitorThresholds.base)}
                </small>
              </div>
              <div
                className={`text-center ${getPlanLabelClass('popular')}`}
                style={{ width: '25%' }}
              >
                <div className="border-end border-2" style={{ height: '10px' }}></div>
                <span className="d-block mt-1">Popular</span>
                <small>
                  {formatVisitors(visitorThresholds.base + 1)}-
                  {formatVisitors(visitorThresholds.popular)}
                </small>
              </div>
              <div
                className={`text-center ${getPlanLabelClass('enterprise')}`}
                style={{ width: '25%' }}
              >
                <span className="d-block mt-1">Enterprise</span>
                <small>{formatVisitors(visitorThresholds.popular + 1)}+</small>
              </div>
            </div>
          </div>

          {/* Billing Period Toggle */}
          <div className="pricing-switch-container" data-component-name="Pricing">
            <span className={`pricing-period-label ${!isAnnual ? 'active' : ''}`}>Monthly</span>
            <label className="pricing-switch">
              <input
                type="checkbox"
                checked={isAnnual}
                onChange={toggleBillingPeriod}
                data-component-name="Pricing"
              />
              <span className="pricing-slider"></span>
            </label>
            <span className={`pricing-period-label ${isAnnual ? 'active' : ''}`}>Yearly</span>
          </div>

          {/* Pricing Cards */}
          <div className="row g-4 justify-content-center">
            {/* Intro Plan */}
            <div className="col-xl-3 col-lg-6 col-md-6 col-sm-12 mb-4">
              <div
                className={`card h-100 border-0 ${
                  recommendedPlan === 'intro' ? 'shadow-lg transform-card' : 'shadow-sm'
                } ${recommendedPlan === 'intro' ? 'bg-primary text-white' : ''}`}
              >
                {recommendedPlan === 'intro' && (
                  <div className="recommended-badge">
                    <span className="badge position-absolute top-0 start-50 translate-middle px-4 py-2 rounded-pill">
                      Recommended
                    </span>
                  </div>
                )}
                <div className="card-body p-4">
                  <h3 className="card-title fw-bold mb-3">Intro</h3>
                  <div className="pricing-value mb-3">
                    <h2 className="fw-bold mb-0">Free</h2>
                    <p className="text-muted">forever</p>
                  </div>
                  <p className="card-text mb-3">For small teams getting started</p>
                  <ul className="list-unstyled mb-4">
                    <li className="mb-2">
                      <i className="bi bi-check-circle me-2"></i>Web chatbot
                    </li>
                    <li className="mb-2">
                      <i className="bi bi-check-circle me-2"></i>100 Visits/month
                    </li>
                    <li className="mb-2">
                      <i className="bi bi-check-circle me-2"></i>Basic analytics
                    </li>
                    <li className="mb-2">
                      <i className="bi bi-check-circle me-2"></i>Email support
                    </li>
                    <li className="mb-2">
                      <i className="bi bi-x-circle me-2"></i>WhatsApp integration
                    </li>
                  </ul>
                  <button
                    className={`btn ${
                      recommendedPlan === 'intro' ? 'btn-primary' : 'btn-outline-primary'
                    } w-100 rounded-pill`}
                  >
                    <a
                      href="https://app.aichatassist.com/auth/sign-in"
                      rel="noopener noreferrer"
                      target="_blank"
                    >
                      Get Started Free
                    </a>
                  </button>
                </div>
              </div>
            </div>

            {/* Base Plan */}
            <div className="col-xl-3 col-lg-6 col-md-6 col-sm-12 mb-4">
              <div
                className={`card h-100 border-0 ${
                  recommendedPlan === 'base' ? 'shadow-lg transform-card' : 'shadow-sm'
                } ${recommendedPlan === 'base' ? 'bg-primary text-white' : ''}`}
              >
                {recommendedPlan === 'base' && (
                  <div className="recommended-badge">
                    <span className="badge position-absolute top-0 start-50 translate-middle px-4 py-2 rounded-pill">
                      Recommended
                    </span>
                  </div>
                )}
                <div className="card-body p-4">
                  <h3 className="card-title fw-bold mb-3">Base</h3>
                  <div className="pricing-value mb-3">
                    <h2 className="fw-bold mb-0">${getPrice('base')}</h2>
                    <p className="text-muted">/ {getBillingText()}</p>
                  </div>
                  <p className="card-text mb-3">For businesses with growing needs</p>
                  <ul className="list-unstyled mb-4">
                    <li className="mb-2">
                      <i className="bi bi-check-circle me-2"></i>Web chatbot
                    </li>
                    <li className="mb-2">
                      <i className="bi bi-check-circle me-2"></i>Upto 1,000 Visits/month
                    </li>
                    <li className="mb-2">
                      <i className="bi bi-check-circle me-2"></i>Standard analytics
                    </li>
                    <li className="mb-2">
                      <i className="bi bi-check-circle me-2"></i>Custom branding
                    </li>
                    <li className="mb-2">
                      <i className="bi bi-check-circle me-2"></i>Basic knowledge base
                    </li>
                  </ul>
                  <button
                    className={`btn ${
                      recommendedPlan === 'base' ? 'btn-primary' : 'btn-outline-primary'
                    } w-100 rounded-pill`}
                  >
                    <a
                      href="https://app.aichatassist.com/auth/sign-in"
                      rel="noopener noreferrer"
                      target="_blank"
                    >
                      Choose Plan
                    </a>
                  </button>
                </div>
              </div>
            </div>

            {/* Popular Plan */}
            <div className="col-xl-3 col-lg-6 col-md-6 col-sm-12 mb-4">
              <div
                className={`card h-100 border-0 ${
                  recommendedPlan === 'popular' ? 'shadow-lg transform-card' : 'shadow-sm'
                } ${recommendedPlan === 'popular' ? 'bg-primary text-white' : ''}`}
              >
                {recommendedPlan === 'popular' && (
                  <div className="recommended-badge">
                    <span className="badge position-absolute top-0 start-50 translate-middle px-4 py-2 rounded-pill">
                      Recommended
                    </span>
                  </div>
                )}
                <div className="card-body p-4">
                  <h3 className="card-title fw-bold mb-3">Popular</h3>
                  <div className="pricing-value mb-3">
                    <h2 className="fw-bold mb-0">${getPrice('popular')}</h2>
                    <p className={recommendedPlan === 'popular' ? 'text-white-50' : 'text-muted'}>
                      / {getBillingText()}
                    </p>
                  </div>
                  <p className="card-text mb-3">For growing teams with advanced needs</p>
                  <ul className="list-unstyled mb-4">
                    <li className="mb-2">
                      <i className="bi bi-check-circle me-2"></i>Upto 5,000 Visits/month
                    </li>
                    <li className="mb-2">
                      <i className="bi bi-check-circle me-2"></i>Advanced analytics
                    </li>
                    <li className="mb-2">
                      <i className="bi bi-check-circle me-2"></i>WhatsApp integration
                    </li>
                    <li className="mb-2">
                      <i className="bi bi-check-circle me-2"></i>Priority support
                    </li>
                    <li className="mb-2">
                      <i className="bi bi-check-circle me-2"></i>Limited API access
                    </li>
                  </ul>
                  <button
                    className={`btn ${
                      recommendedPlan === 'popular' ? 'btn-primary' : 'btn-outline-primary'
                    } w-100 rounded-pill`}
                  >
                    <a
                      href="https://app.aichatassist.com/auth/sign-in"
                      rel="noopener noreferrer"
                      target="_blank"
                    >
                      Choose Plan
                    </a>
                  </button>
                </div>
              </div>
            </div>

            {/* Enterprise Plan */}
            <div className="col-xl-3 col-lg-6 col-md-6 col-sm-12 mb-4">
              <div
                className={`card h-100 border-0 ${
                  recommendedPlan === 'enterprise' ? 'shadow-lg transform-card' : 'shadow-sm'
                } ${recommendedPlan === 'enterprise' ? 'bg-primary text-white' : ''}`}
              >
                {recommendedPlan === 'enterprise' && (
                  <div className="recommended-badge">
                    <span className="badge position-absolute top-0 start-50 translate-middle px-4 py-2 rounded-pill">
                      Recommended
                    </span>
                  </div>
                )}
                <div className="card-body p-4">
                  <h3 className="card-title fw-bold mb-3">Enterprise</h3>
                  <div className="pricing-value mb-3">
                    <h2 className="fw-bold mb-0">${getPrice('enterprise')}</h2>
                    <p className="text-muted">/ {getBillingText()}</p>
                  </div>
                  <p className="card-text mb-3">For large organizations with complex needs</p>
                  <ul className="list-unstyled mb-4">
                    <li className="mb-2">
                      <i className="bi bi-check-circle me-2"></i>Upto 10000 Visits
                    </li>
                    <li className="mb-2">
                      <i className="bi bi-check-circle me-2"></i>Custom analytics
                    </li>
                    <li className="mb-2">
                      <i className="bi bi-check-circle me-2"></i>Full API access
                    </li>
                    <li className="mb-2">
                      <i className="bi bi-check-circle me-2"></i>24/7 dedicated support
                    </li>
                    <li className="mb-2">
                      <i className="bi bi-check-circle me-2"></i>Custom API integration
                    </li>
                  </ul>
                  <button
                    className={`btn ${
                      recommendedPlan === 'enterprise' ? 'btn-primary' : 'btn-outline-primary'
                    } w-100 rounded-pill`}
                  >
                    <a
                      href="https://app.aichatassist.com/auth/sign-in"
                      rel="noopener noreferrer"
                      target="_blank"
                    >
                      Choose Plan
                    </a>
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Custom CSS for transform effect */}
          <style jsx="true">{`
            .transform-card {
              transform: scale(1.05);
              z-index: 1;
              transition: all 0.3s ease;
              transform: translateY(-10px);
              box-shadow: 0 15px 35px rgba(0, 153, 255, 0.3);
              z-index: 5;
              background: var(--purple-gradient);
            }
            .transform-card .card-title {
              color: white;
            }
            .transform-card .card-text {
              color: white;
            }
            .transform-card .btn {
              background: white;
              color: var(--primary-color);
            }
            .transform-card .pricing-value h2 {
              color: white;
            }
            .transform-card .pricing-value p {
              color: white !important;
            }
            .recommended-badge {
              height: 30px;
            }
            .recommended-badge .badge {
              background: white;
              color: var(--primary-color);
            }
          `}</style>
        </div>
      </div>

      {/* Feature Comparison */}
      <section className="feature-comparison-section py-5">
        <div className="container fade-in">
          <h2 className="text-center fw-bold mb-5">Feature Comparison</h2>
          <div className="row">
            <div className="col-12">
              <div className="comparison-table">
                {/* Table Header */}
                <div className="row mb-4">
                  <div className="col-3">
                    <h5 className="fw-bold text-uppercase text-muted mb-0 mt-2">Features</h5>
                  </div>
                  <div className="col-2 text-center">
                    <div className="pricing-tier team">
                      <h5 className="fw-bold mb-2">Intro</h5>
                      <p className="small mb-3">For small teams</p>
                      <h3 className="fw-bold mb-1">${getPrice('intro')}</h3>
                      <p className="small text-muted mb-3">/ {getBillingText()}</p>
                      <button
                        className="btn btn-primary btn-sm rounded-pill w-100"
                        style={{ fontSize: '0.8rem' }}
                      >
                        <a
                          href="https://app.aichatassist.com/auth/sign-in"
                          rel="noopener noreferrer"
                          target="_blank"
                        >
                          GET STARTED
                        </a>
                      </button>
                    </div>
                  </div>
                  <div className="col-2 text-center">
                    <div className="pricing-tier organization">
                      <h5 className="fw-bold mb-2">Base</h5>
                      <p className="small mb-3">For businesses</p>
                      <h3 className="fw-bold mb-1">${getPrice('base')}</h3>
                      <p className="small text-muted mb-3">/ {getBillingText()}</p>
                      <button
                        className="btn btn-primary btn-sm rounded-pill w-100"
                        style={{ fontSize: '0.8rem' }}
                      >
                        <a
                          href="https://app.aichatassist.com/auth/sign-in"
                          rel="noopener noreferrer"
                          target="_blank"
                        >
                          GET STARTED
                        </a>
                      </button>
                    </div>
                  </div>
                  <div className="col-2 text-center">
                    <div className="pricing-tier popular">
                      <h5 className="fw-bold mb-2">Popular</h5>
                      <p className="small mb-3">For growing teams</p>
                      <h3 className="fw-bold mb-1">${getPrice('popular')}</h3>
                      <p className="small text-muted mb-3">/ {getBillingText()}</p>
                      <button
                        className="btn btn-primary btn-sm rounded-pill w-100"
                        style={{ fontSize: '0.8rem' }}
                      >
                        <a
                          href="https://app.aichatassist.com/auth/sign-in"
                          rel="noopener noreferrer"
                          target="_blank"
                        >
                          GET STARTED
                        </a>
                      </button>
                    </div>
                  </div>
                  <div className="col-2 text-center">
                    <div className="pricing-tier enterprise">
                      <h5 className="fw-bold mb-2">Enterprise</h5>
                      <p className="small mb-3">For large orgs</p>
                      <h3 className="fw-bold mb-1">${getPrice('enterprise')}</h3>
                      <p className="small text-muted mb-3">/ {getBillingText()}</p>
                      <button
                        className="btn btn-primary btn-sm rounded-pill w-100"
                        style={{ fontSize: '0.8rem' }}
                      >
                        <a
                          href="https://app.aichatassist.com/auth/sign-in"
                          rel="noopener noreferrer"
                          target="_blank"
                        >
                          GET STARTED
                        </a>
                      </button>
                    </div>
                  </div>
                </div>

                {/* Basic Features */}
                <div className="feature-group mb-4">
                  <div className="row mb-3">
                    <div className="col-12">
                      <h6 className="text-uppercase text-muted">Basic Features</h6>
                    </div>
                  </div>
                  <div className="row feature-row py-2">
                    <div className="col-3">
                      <OverlayTrigger
                        placement="top"
                        overlay={
                          <Tooltip>
                            A chat assistant that appears on your website to help visitors get
                            information and answer questions
                          </Tooltip>
                        }
                      >
                        <span className="feature-name info-tooltip">
                          Web Chatbot{' '}
                          <i className="bi bi-info-circle-fill text-primary info-icon"></i>
                        </span>
                      </OverlayTrigger>
                    </div>
                    <div className="col-2 text-center">
                      <i className="bi bi-check-lg text-success"></i>
                    </div>
                    <div className="col-2 text-center">
                      <i className="bi bi-check-lg text-success"></i>
                    </div>
                    <div className="col-2 text-center">
                      <i className="bi bi-check-lg text-success"></i>
                    </div>
                    <div className="col-2 text-center">
                      <i className="bi bi-check-lg text-success"></i>
                    </div>
                  </div>
                  <div className="row feature-row py-2">
                    <div className="col-3">
                      <OverlayTrigger
                        placement="top"
                        overlay={
                          <Tooltip>
                            A bot that provides basic information about your business, products, and
                            services
                          </Tooltip>
                        }
                      >
                        <span className="feature-name info-tooltip">
                          Information Bot{' '}
                          <i className="bi bi-info-circle-fill text-primary info-icon"></i>
                        </span>
                      </OverlayTrigger>
                    </div>
                    <div className="col-2 text-center">
                      <i className="bi bi-check-lg text-success"></i>
                    </div>
                    <div className="col-2 text-center">
                      <i className="bi bi-check-lg text-success"></i>
                    </div>
                    <div className="col-2 text-center">
                      <i className="bi bi-check-lg text-success"></i>
                    </div>
                    <div className="col-2 text-center">
                      <i className="bi bi-check-lg text-success"></i>
                    </div>
                  </div>
                  <div className="row feature-row py-2">
                    <div className="col-3">
                      <span className="feature-name">Monthly Visits</span>
                    </div>
                    <div className="col-2 text-center">
                      <span className="badge bg-secondary">100</span>
                    </div>
                    <div className="col-2 text-center">
                      <span className="badge bg-secondary">1,000</span>
                    </div>
                    <div className="col-2 text-center">
                      <span className="badge bg-primary">5,000</span>
                    </div>
                    <div className="col-2 text-center">
                      <span className="badge bg-dark">10,000+</span>
                    </div>
                  </div>
                  <div className="row feature-row py-2">
                    <div className="col-3">
                      <p className="mb-0">Custom Branding</p>
                    </div>
                    <div className="col-2 text-center">
                      <i className="bi bi-x-circle text-muted"></i>
                    </div>
                    <div className="col-2 text-center">
                      <i className="bi bi-check-lg text-success"></i>
                    </div>
                    <div className="col-2 text-center">
                      <i className="bi bi-check-lg text-success"></i>
                    </div>
                    <div className="col-2 text-center">
                      <i className="bi bi-check-lg text-success"></i>
                    </div>
                  </div>
                </div>

                {/* Core Features */}
                <div className="feature-group mb-4">
                  <div className="row mb-3">
                    <div className="col-12">
                      <h6 className="text-uppercase text-muted">Core Features</h6>
                    </div>
                  </div>
                  <div className="row feature-row py-2">
                    <div className="col-3">
                      <OverlayTrigger
                        placement="top"
                        overlay={
                          <Tooltip>
                            Create custom conversation paths and automated responses based on user
                            inputs
                          </Tooltip>
                        }
                      >
                        <span className="feature-name info-tooltip">
                          Conversation Flows{' '}
                          <i className="bi bi-info-circle-fill text-primary info-icon"></i>
                        </span>
                      </OverlayTrigger>
                    </div>
                    <div className="col-2 text-center">
                      <i className="bi bi-x-circle text-muted"></i>
                    </div>
                    <div className="col-2 text-center">
                      <i className="bi bi-check-lg text-success"></i>
                    </div>
                    <div className="col-2 text-center">
                      <i className="bi bi-check-lg text-success"></i>
                    </div>
                    <div className="col-2 text-center">
                      <i className="bi bi-check-lg text-success"></i>
                    </div>
                  </div>
                  <div className="row feature-row py-2">
                    <div className="col-3">
                      <OverlayTrigger
                        placement="top"
                        overlay={
                          <Tooltip>
                            Support for multiple languages to communicate with customers worldwide
                          </Tooltip>
                        }
                      >
                        <span className="feature-name info-tooltip">
                          Multiple Languages{' '}
                          <i className="bi bi-info-circle-fill text-primary info-icon"></i>
                        </span>
                      </OverlayTrigger>
                    </div>
                    <div className="col-2 text-center">
                      <i className="bi bi-x-circle text-muted"></i>
                    </div>
                    <div className="col-2 text-center">
                      <span className="badge bg-light text-dark">3</span>
                    </div>
                    <div className="col-2 text-center">
                      <span className="badge bg-light text-dark">5</span>
                    </div>
                    <div className="col-2 text-center">
                      <span className="badge bg-dark">15+</span>
                    </div>
                  </div>
                  <div className="row feature-row py-2">
                    <div className="col-3">
                      <span className="feature-name">Analytics</span>
                    </div>
                    <div className="col-2 text-center">
                      <span className="badge bg-secondary">Basic</span>
                    </div>
                    <div className="col-2 text-center">
                      <span className="badge bg-secondary">Standard</span>
                    </div>
                    <div className="col-2 text-center">
                      <span className="badge bg-primary">Advanced</span>
                    </div>
                    <div className="col-2 text-center">
                      <span className="badge bg-dark">Custom</span>
                    </div>
                  </div>
                </div>

                {/* WhatsApp Bot Features */}
                <div className="feature-group mb-4">
                  <div className="row mb-3">
                    <div className="col-12">
                      <h6 className="text-uppercase text-muted">WhatsApp Bot Features</h6>
                    </div>
                  </div>
                  <div className="row feature-row py-2">
                    <div className="col-3">
                      <OverlayTrigger
                        placement="top"
                        overlay={
                          <Tooltip>
                            Connect your chatbot to WhatsApp so customers can chat with you there
                          </Tooltip>
                        }
                      >
                        <p className="mb-0 info-tooltip">
                          WhatsApp Connection{' '}
                          <i className="bi bi-info-circle-fill text-primary info-icon"></i>
                        </p>
                      </OverlayTrigger>
                    </div>
                    <div className="col-2 text-center">
                      <i className="bi bi-x-circle text-muted"></i>
                    </div>
                    <div className="col-2 text-center">
                      <i className="bi bi-check-lg text-success"></i>
                    </div>
                    <div className="col-2 text-center">
                      <i className="bi bi-check-lg text-success"></i>
                    </div>
                    <div className="col-2 text-center">
                      <i className="bi bi-check-lg text-success"></i>
                    </div>
                  </div>
                  <div className="row feature-row py-2">
                    <div className="col-3">
                      <OverlayTrigger
                        placement="top"
                        overlay={
                          <Tooltip>
                            Manage multiple business profiles and campaigns from a single dashboard
                            with comprehensive analytics and reporting
                          </Tooltip>
                        }
                      >
                        <p className="mb-0 info-tooltip">
                          Portfolio Management{' '}
                          <span
                            className="badge ms-1"
                            style={{
                              fontSize: '0.7rem',
                              background: 'linear-gradient(135deg, #a17ffd 0%, #7a4bff 100%)',
                              color: 'white',
                            }}
                          >
                            Coming Soon
                          </span>{' '}
                          <i className="bi bi-info-circle-fill text-primary info-icon"></i>
                        </p>
                      </OverlayTrigger>
                    </div>
                    <div className="col-2 text-center">
                      <i className="bi bi-dash-circle text-muted"></i>
                    </div>
                    <div className="col-2 text-center">
                      <i className="bi bi-dash-circle text-muted"></i>
                    </div>
                    <div className="col-2 text-center">
                      <i className="bi bi-dash-circle text-muted"></i>
                    </div>
                    <div className="col-2 text-center">
                      <i className="bi bi-dash-circle text-muted"></i>
                    </div>
                  </div>
                  <div className="row feature-row py-2">
                    <div className="col-3">
                      <OverlayTrigger
                        placement="top"
                        overlay={
                          <Tooltip>
                            Connect your bot to AI systems for smarter responses and better
                            understanding of customer questions
                          </Tooltip>
                        }
                      >
                        <p className="mb-0 info-tooltip">
                          Smart AI Connection{' '}
                          <i className="bi bi-info-circle-fill text-primary info-icon"></i>
                        </p>
                      </OverlayTrigger>
                    </div>
                    <div className="col-2 text-center">
                      <i className="bi bi-x-circle text-muted"></i>
                    </div>
                    <div className="col-2 text-center">
                      <i className="bi bi-check-lg text-success"></i>
                    </div>
                    <div className="col-2 text-center">
                      <i className="bi bi-check-lg text-success"></i>
                    </div>
                    <div className="col-2 text-center">
                      <i className="bi bi-check-lg text-success"></i>
                    </div>
                  </div>
                  <div className="row feature-row py-2">
                    <div className="col-3">
                      <OverlayTrigger
                        placement="top"
                        overlay={
                          <Tooltip>
                            Get your business verified with a blue tick badge to build trust with
                            customers and improve engagement rates
                          </Tooltip>
                        }
                      >
                        <p className="mb-0 info-tooltip">
                          Blue Tick Setup{' '}
                          <span
                            className="badge ms-1"
                            style={{
                              fontSize: '0.7rem',
                              background: 'linear-gradient(135deg, #a17ffd 0%, #7a4bff 100%)',
                              color: 'white',
                            }}
                          >
                            Coming Soon
                          </span>{' '}
                          <i className="bi bi-info-circle-fill text-primary info-icon"></i>
                        </p>
                      </OverlayTrigger>
                    </div>
                    <div className="col-2 text-center">
                      <i className="bi bi-dash-circle text-muted"></i>
                    </div>
                    <div className="col-2 text-center">
                      <i className="bi bi-dash-circle text-muted"></i>
                    </div>
                    <div className="col-2 text-center">
                      <i className="bi bi-dash-circle text-muted"></i>
                    </div>
                    <div className="col-2 text-center">
                      <i className="bi bi-dash-circle text-muted"></i>
                    </div>
                  </div>
                  <div className="row feature-row py-2">
                    <div className="col-3">
                      <p className="mb-0">Lead Management</p>
                    </div>
                    <div className="col-2 text-center">
                      <i className="bi bi-x-circle text-muted"></i>
                    </div>
                    <div className="col-2 text-center">
                      <i className="bi bi-x-circle text-muted"></i>
                    </div>
                    <div className="col-2 text-center">
                      <i className="bi bi-check-lg text-success"></i>
                    </div>
                    <div className="col-2 text-center">
                      <i className="bi bi-check-lg text-success"></i>
                    </div>
                  </div>
                  <div className="row feature-row py-2">
                    <div className="col-3">
                      <OverlayTrigger
                        placement="top"
                        overlay={
                          <Tooltip>
                            Automatically respond to customer inquiries and schedule follow-up
                            messages to nurture leads and improve conversion rates
                          </Tooltip>
                        }
                      >
                        <p className="mb-0 info-tooltip">
                          Autoreply & Follow-ups{' '}
                          <span
                            className="badge ms-1"
                            style={{
                              fontSize: '0.7rem',
                              background: 'linear-gradient(135deg, #a17ffd 0%, #7a4bff 100%)',
                              color: 'white',
                            }}
                          >
                            Coming Soon
                          </span>{' '}
                          <i className="bi bi-info-circle-fill text-primary info-icon"></i>
                        </p>
                      </OverlayTrigger>
                    </div>
                    <div className="col-2 text-center">
                      <i className="bi bi-dash-circle text-muted"></i>
                    </div>
                    <div className="col-2 text-center">
                      <i className="bi bi-dash-circle text-muted"></i>
                    </div>
                    <div className="col-2 text-center">
                      <i className="bi bi-dash-circle text-muted"></i>
                    </div>
                    <div className="col-2 text-center">
                      <i className="bi bi-dash-circle text-muted"></i>
                    </div>
                  </div>
                  <div className="row feature-row py-2">
                    <div className="col-3">
                      <p className="mb-0">Multi-number Setup</p>
                    </div>
                    <div className="col-2 text-center">
                      <i className="bi bi-x-circle text-muted"></i>
                    </div>
                    <div className="col-2 text-center">
                      <i className="bi bi-x-circle text-muted"></i>
                    </div>
                    <div className="col-2 text-center">
                      <i className="bi bi-check-lg text-success"></i>
                    </div>
                    <div className="col-2 text-center">
                      <i className="bi bi-check-lg text-success"></i>
                    </div>
                  </div>
                </div>

                {/* Advanced Features */}
                <div className="feature-group mb-4">
                  <div className="row mb-3">
                    <div className="col-12">
                      <h6 className="text-uppercase text-muted">Advanced Features</h6>
                    </div>
                  </div>
                  <div className="row feature-row py-2">
                    <div className="col-3">
                      <p className="mb-0">Visual Builder</p>
                    </div>
                    <div className="col-2 text-center">
                      <i className="bi bi-x-circle text-muted"></i>
                    </div>
                    <div className="col-2 text-center">
                      <i className="bi bi-x-circle text-muted"></i>
                    </div>
                    <div className="col-2 text-center">
                      <i className="bi bi-check-lg text-success"></i>
                    </div>
                    <div className="col-2 text-center">
                      <i className="bi bi-check-lg text-success"></i>
                    </div>
                  </div>
                  <div className="row feature-row py-2">
                    <div className="col-3">
                      <p className="mb-0">Human Handoff</p>
                    </div>
                    <div className="col-2 text-center">
                      <i className="bi bi-x-circle text-muted"></i>
                    </div>
                    <div className="col-2 text-center">
                      <i className="bi bi-x-circle text-muted"></i>
                    </div>
                    <div className="col-2 text-center">
                      <i className="bi bi-check-lg text-success"></i>
                    </div>
                    <div className="col-2 text-center">
                      <i className="bi bi-check-lg text-success"></i>
                    </div>
                  </div>
                  <div className="row feature-row py-2">
                    <div className="col-3">
                      <OverlayTrigger
                        placement="top"
                        overlay={
                          <Tooltip>
                            Connect your chatbot to your other business systems like CRM, inventory,
                            or booking systems
                          </Tooltip>
                        }
                      >
                        <p className="mb-0 info-tooltip">
                          Connect to Your Systems{' '}
                          <i className="bi bi-info-circle-fill text-primary info-icon"></i>
                        </p>
                      </OverlayTrigger>
                    </div>
                    <div className="col-2 text-center">
                      <i className="bi bi-x-circle text-muted"></i>
                    </div>
                    <div className="col-2 text-center">
                      <i className="bi bi-x-circle text-muted"></i>
                    </div>
                    <div className="col-2 text-center">
                      <i className="bi bi-check-lg text-success"></i>
                    </div>
                    <div className="col-2 text-center">
                      <span className="badge bg-dark">Full</span>
                    </div>
                  </div>
                  <div className="row feature-row py-2">
                    <div className="col-3">
                      <OverlayTrigger
                        placement="top"
                        overlay={
                          <Tooltip>
                            Train the AI with your specific business information to give more
                            accurate and relevant answers
                          </Tooltip>
                        }
                      >
                        <p className="mb-0 info-tooltip">
                          Custom AI Training{' '}
                          <i className="bi bi-info-circle-fill text-primary info-icon"></i>
                        </p>
                      </OverlayTrigger>
                    </div>
                    <div className="col-2 text-center">
                      <i className="bi bi-x-circle text-muted"></i>
                    </div>
                    <div className="col-2 text-center">
                      <i className="bi bi-x-circle text-muted"></i>
                    </div>
                    <div className="col-2 text-center">
                      <span className="badge bg-primary">Limited</span>
                    </div>
                    <div className="col-2 text-center">
                      <span className="badge bg-dark">Full</span>
                    </div>
                  </div>
                  <div className="row feature-row py-2">
                    <div className="col-3">
                      <OverlayTrigger
                        placement="top"
                        overlay={
                          <Tooltip>
                            Connect your chatbot to your help documents, FAQs, and other resources
                            to answer questions accurately
                          </Tooltip>
                        }
                      >
                        <p className="mb-0 info-tooltip">
                          Help Document Access{' '}
                          <i className="bi bi-info-circle-fill text-primary info-icon"></i>
                        </p>
                      </OverlayTrigger>
                    </div>
                    <div className="col-2 text-center">
                      <i className="bi bi-x-circle text-muted"></i>
                    </div>
                    <div className="col-2 text-center">
                      <i className="bi bi-check-lg text-success"></i>
                    </div>
                    <div className="col-2 text-center">
                      <span className="badge bg-primary">Advanced</span>
                    </div>
                    <div className="col-2 text-center">
                      <span className="badge bg-dark">Enterprise</span>
                    </div>
                  </div>
                  <div className="row feature-row py-2">
                    <div className="col-3">
                      <p className="mb-0">Authentication Bot</p>
                    </div>
                    <div className="col-2 text-center">
                      <i className="bi bi-x-circle text-muted"></i>
                    </div>
                    <div className="col-2 text-center">
                      <i className="bi bi-x-circle text-muted"></i>
                    </div>
                    <div className="col-2 text-center">
                      <i className="bi bi-x-circle text-muted"></i>
                    </div>
                    <div className="col-2 text-center">
                      <span className="badge bg-dark">Advanced</span>
                    </div>
                  </div>
                  <div className="row feature-row py-2">
                    <div className="col-3">
                      <p className="mb-0">Ramp-up System</p>
                    </div>
                    <div className="col-2 text-center">
                      <i className="bi bi-x-circle text-muted"></i>
                    </div>
                    <div className="col-2 text-center">
                      <i className="bi bi-x-circle text-muted"></i>
                    </div>
                    <div className="col-2 text-center">
                      <i className="bi bi-x-circle text-muted"></i>
                    </div>
                    <div className="col-2 text-center">
                      <i className="bi bi-check-lg text-success"></i>
                    </div>
                  </div>
                </div>

                {/* WhatsApp Campaign */}
                <div className="feature-group mb-4">
                  <div className="row mb-3">
                    <div className="col-12">
                      <h6 className="text-uppercase text-muted">WhatsApp Campaign</h6>
                    </div>
                  </div>
                  <div className="row feature-row py-2">
                    <div className="col-3">
                      <p className="mb-0">Easy Campaign</p>
                    </div>
                    <div className="col-2 text-center">
                      <i className="bi bi-x-circle text-muted"></i>
                    </div>
                    <div className="col-2 text-center">
                      <i className="bi bi-check-lg text-success"></i>
                    </div>
                    <div className="col-2 text-center">
                      <i className="bi bi-check-lg text-success"></i>
                    </div>
                    <div className="col-2 text-center">
                      <i className="bi bi-check-lg text-success"></i>
                    </div>
                  </div>
                  <div className="row feature-row py-2">
                    <div className="col-3">
                      <OverlayTrigger
                        placement="top"
                        overlay={
                          <Tooltip>
                            Run campaigns across multiple phone numbers simultaneously to maximize
                            reach and engagement with targeted audience segments
                          </Tooltip>
                        }
                      >
                        <p className="mb-0 info-tooltip">
                          Multinumber Campaign{' '}
                          <span
                            className="badge ms-1"
                            style={{
                              fontSize: '0.7rem',
                              background: 'linear-gradient(135deg, #a17ffd 0%, #7a4bff 100%)',
                              color: 'white',
                            }}
                          >
                            Coming Soon
                          </span>{' '}
                          <i className="bi bi-info-circle-fill text-primary info-icon"></i>
                        </p>
                      </OverlayTrigger>
                    </div>
                    <div className="col-2 text-center">
                      <i className="bi bi-dash-circle text-muted"></i>
                    </div>
                    <div className="col-2 text-center">
                      <i className="bi bi-dash-circle text-muted"></i>
                    </div>
                    <div className="col-2 text-center">
                      <i className="bi bi-dash-circle text-muted"></i>
                    </div>
                    <div className="col-2 text-center">
                      <i className="bi bi-dash-circle text-muted"></i>
                    </div>
                  </div>
                  <div className="row feature-row py-2">
                    <div className="col-3">
                      <p className="mb-0">Reputation Helper</p>
                    </div>
                    <div className="col-2 text-center">
                      <i className="bi bi-x-circle text-muted"></i>
                    </div>
                    <div className="col-2 text-center">
                      <i className="bi bi-x-circle text-muted"></i>
                    </div>
                    <div className="col-2 text-center">
                      <i className="bi bi-x-circle text-muted"></i>
                    </div>
                    <div className="col-2 text-center">
                      <i className="bi bi-check-lg text-success"></i>
                    </div>
                  </div>
                  <div className="row feature-row py-2">
                    <div className="col-3">
                      <OverlayTrigger
                        placement="top"
                        overlay={
                          <Tooltip>
                            Leverage AI to optimize your campaigns in real-time, automatically
                            adjusting messaging and targeting based on performance metrics
                          </Tooltip>
                        }
                      >
                        <p className="mb-0 info-tooltip">
                          AI Campaign Manager{' '}
                          <span
                            className="badge ms-1"
                            style={{
                              fontSize: '0.7rem',
                              background: 'linear-gradient(135deg, #a17ffd 0%, #7a4bff 100%)',
                              color: 'white',
                            }}
                          >
                            Coming Soon
                          </span>{' '}
                          <i className="bi bi-info-circle-fill text-primary info-icon"></i>
                        </p>
                      </OverlayTrigger>
                    </div>
                    <div className="col-2 text-center">
                      <i className="bi bi-dash-circle text-muted"></i>
                    </div>
                    <div className="col-2 text-center">
                      <i className="bi bi-dash-circle text-muted"></i>
                    </div>
                    <div className="col-2 text-center">
                      <i className="bi bi-dash-circle text-muted"></i>
                    </div>
                    <div className="col-2 text-center">
                      <i className="bi bi-dash-circle text-muted"></i>
                    </div>
                  </div>
                </div>

                {/* Support */}
                <div className="feature-group">
                  <div className="row mb-3">
                    <div className="col-12">
                      <h6 className="text-uppercase text-muted">Support</h6>
                    </div>
                  </div>
                  <div className="row feature-row py-2">
                    <div className="col-3">
                      <p className="mb-0">Support Level</p>
                    </div>
                    <div className="col-2 text-center">
                      <span className="badge bg-light text-dark">Community</span>
                    </div>
                    <div className="col-2 text-center">
                      <span className="badge bg-light text-dark">Email</span>
                    </div>
                    <div className="col-2 text-center">
                      <span className="badge bg-primary">Priority</span>
                    </div>
                    <div className="col-2 text-center">
                      <span className="badge bg-dark">24/7 Dedicated</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Add custom CSS for the pricing table */}
      <style jsx="true">{`
        .comparison-table {
          background-color: white;
          border-radius: 12px;
          box-shadow: 0 0.5rem 1.5rem rgba(0, 0, 0, 0.08);
          padding: 2.5rem;
          overflow: hidden;
        }
        .pricing-tier {
          padding: 1rem;
          border-radius: 12px;
          transition: all 0.3s ease;
          cursor: pointer;
          position: relative;
          overflow: hidden;
          z-index: 1;
        }
        .pricing-tier:before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: var(--purple-gradient);
          opacity: 0;
          z-index: -1;
          transition: opacity 0.3s ease;
        }
        .pricing-tier:hover {
          transform: translateY(-5px);
          box-shadow: 0 10px 20px rgba(140, 111, 255, 0.25);
          border-color: var(--primary-color);
        }
        .pricing-tier:hover:before {
          opacity: 0.08;
        }
        .pricing-tier:hover h5,
        .pricing-tier:hover h3 {
          color: var(--primary-color);
        }
        .pricing-tier:hover .btn-primary {
          background: var(--purple-gradient);
          border-color: var(--primary-color);
          box-shadow: 0 4px 10px rgba(140, 111, 255, 0.3);
        }
        .pricing-tier {
          position: relative;
          overflow: hidden;
        }
        .pricing-tier:after {
          content: '';
          position: absolute;
          z-index: -2;
          border-radius: 50%;
          opacity: 0.15;
        }
        .pricing-tier.team {
          background-color: rgba(140, 111, 255, 0.05);
          border: 1px solid rgba(140, 111, 255, 0.2);
        }
        .pricing-tier.team:after {
          width: 150px;
          height: 150px;
          background-color: rgba(140, 111, 255, 0.2);
          bottom: -70px;
          right: -70px;
        }
        .pricing-tier.business {
          background-color: rgba(180, 144, 255, 0.05);
          border: 1px solid rgba(180, 144, 255, 0.2);
        }
        .pricing-tier.business:after {
          width: 120px;
          height: 120px;
          background-color: rgba(180, 144, 255, 0.2);
          top: -60px;
          right: -60px;
        }
        .pricing-tier.organization {
          background-color: rgba(206, 117, 255, 0.05);
          border: 1px solid rgba(206, 117, 255, 0.2);
        }
        .pricing-tier.organization:after {
          width: 100px;
          height: 100px;
          background-color: rgba(206, 117, 255, 0.2);
          bottom: -30px;
          left: -30px;
        }
        .pricing-tier.enterprise {
          background-color: rgba(220, 130, 255, 0.05);
          border: 1px solid rgba(220, 130, 255, 0.2);
        }
        .pricing-tier.enterprise:after {
          width: 130px;
          height: 130px;
          background-color: rgba(220, 130, 255, 0.2);
          top: -40px;
          left: -40px;
        }
        .pricing-tier.popular {
          background-color: rgba(206, 117, 255, 0.05);
          border: 1px solid rgba(206, 117, 255, 0.2);
        }
        .feature-group {
          border-bottom: 1px solid #eee;
          padding-bottom: 1.5rem;
          margin-bottom: 1.5rem;
        }
        .feature-group:last-child {
          border-bottom: none;
          padding-bottom: 0;
          margin-bottom: 0;
        }
        .feature-row {
          border-radius: 8px;
          transition: all 0.2s ease;
        }
        .feature-row:hover {
          background-color: rgba(0, 123, 255, 0.05);
        }
        .recommended-badge .badge {
          margin-top: 25px;
        }
        .badge {
          font-weight: 500;
          padding: 0.4rem 0.6rem;
          border-radius: 4px;
        }
      `}</style>

      {/* Demo CTA Section */}
      <DemoCTA />

      {/* FAQ Section */}
      <section className="faq-section py-5">
        <div className="container">
          <div className="text-center mb-5 fade-in">
            <h2 className="fw-bold">Frequently Asked Questions</h2>
            <p className="lead">Find answers to common questions about our pricing and plans</p>
          </div>
          <div className="row justify-content-center">
            <div className="col-lg-8">
              <div className="accordion fade-in" id="pricingFaq">
                <div className="accordion-item border-0 mb-3 shadow-sm">
                  <h2 className="accordion-header" id="headingOne">
                    <button
                      className="accordion-button"
                      type="button"
                      data-bs-toggle="collapse"
                      data-bs-target="#collapseOne"
                      aria-expanded="true"
                      aria-controls="collapseOne"
                    >
                      What happens if I exceed my monthly chatbot visits limit?
                    </button>
                  </h2>
                  <div
                    id="collapseOne"
                    className="accordion-collapse collapse show"
                    aria-labelledby="headingOne"
                    data-bs-parent="#pricingFaq"
                  >
                    <div className="accordion-body">
                      If you exceed your monthly chatbot visits limit, you'll be charged a small fee
                      per additional visit. A visit is counted each time your chatbot is opened by a
                      user. We'll notify you when you reach 80% of your limit so you can decide
                      whether to upgrade your plan or pay for the additional usage. There is no
                      limit on the number of messages exchanged during a single chatbot session.
                    </div>
                  </div>
                </div>
                <div className="accordion-item border-0 mb-3 shadow-sm">
                  <h2 className="accordion-header" id="headingTwo">
                    <button
                      className="accordion-button collapsed"
                      type="button"
                      data-bs-toggle="collapse"
                      data-bs-target="#collapseTwo"
                      aria-expanded="false"
                      aria-controls="collapseTwo"
                    >
                      Can I switch plans at any time?
                    </button>
                  </h2>
                  <div
                    id="collapseTwo"
                    className="accordion-collapse collapse"
                    aria-labelledby="headingTwo"
                    data-bs-parent="#pricingFaq"
                  >
                    <div className="accordion-body">
                      Yes, you can upgrade your plan at any time, and the changes will take effect
                      immediately. If you downgrade, the changes will take effect at the start of
                      your next billing cycle.
                    </div>
                  </div>
                </div>
                <div className="accordion-item border-0 mb-3 shadow-sm">
                  <h2 className="accordion-header" id="headingThree">
                    <button
                      className="accordion-button collapsed"
                      type="button"
                      data-bs-toggle="collapse"
                      data-bs-target="#collapseThree"
                      aria-expanded="false"
                      aria-controls="collapseThree"
                    >
                      Do you offer a free trial?
                    </button>
                  </h2>
                  <div
                    id="collapseThree"
                    className="accordion-collapse collapse"
                    aria-labelledby="headingThree"
                    data-bs-parent="#pricingFaq"
                  >
                    <div className="accordion-body">
                      Yes, we offer a 7-day free trial on all our plans. No credit card is required
                      to start your trial, and you can cancel at any time.
                    </div>
                  </div>
                </div>
                <div className="accordion-item border-0 mb-3 shadow-sm">
                  <h2 className="accordion-header" id="headingFour">
                    <button
                      className="accordion-button collapsed"
                      type="button"
                      data-bs-toggle="collapse"
                      data-bs-target="#collapseFour"
                      aria-expanded="false"
                      aria-controls="collapseFour"
                    >
                      Do you offer discounts for non-profits or educational institutions?
                    </button>
                  </h2>
                  <div
                    id="collapseFour"
                    className="accordion-collapse collapse"
                    aria-labelledby="headingFour"
                    data-bs-parent="#pricingFaq"
                  >
                    <div className="accordion-body">
                      Yes, we offer special pricing for non-profit organizations and educational
                      institutions. Please contact our sales team for more information.
                    </div>
                  </div>
                </div>
                <div className="accordion-item border-0 mb-3 shadow-sm">
                  <h2 className="accordion-header" id="headingFive">
                    <button
                      className="accordion-button collapsed"
                      type="button"
                      data-bs-toggle="collapse"
                      data-bs-target="#collapseFive"
                      aria-expanded="false"
                      aria-controls="collapseFive"
                    >
                      How secure is my data with AI Chat Assist?
                    </button>
                  </h2>
                  <div
                    id="collapseFive"
                    className="accordion-collapse collapse"
                    aria-labelledby="headingFive"
                    data-bs-parent="#pricingFaq"
                  >
                    <div className="accordion-body">
                      Your data security is our top priority. We use enterprise-grade encryption for
                      all data at rest and in transit. Our platform is compliant with GDPR, CCPA,
                      and other relevant data protection regulations. We never share your data with
                      third parties without your explicit consent, and you maintain full ownership
                      of all your data.
                    </div>
                  </div>
                </div>
                <div className="accordion-item border-0 mb-3 shadow-sm">
                  <h2 className="accordion-header" id="headingSix">
                    <button
                      className="accordion-button collapsed"
                      type="button"
                      data-bs-toggle="collapse"
                      data-bs-target="#collapseSix"
                      aria-expanded="false"
                      aria-controls="collapseSix"
                    >
                      What integrations do you support?
                    </button>
                  </h2>
                  <div
                    id="collapseSix"
                    className="accordion-collapse collapse"
                    aria-labelledby="headingSix"
                    data-bs-parent="#pricingFaq"
                  >
                    <div className="accordion-body">
                      AI Chat Assist integrates seamlessly with popular business tools including
                      WhatsApp Business API, Facebook Messenger, Instagram, CRM systems like
                      Salesforce and HubSpot, and e-commerce platforms like Shopify and WooCommerce.
                      We're constantly adding new integrations based on customer feedback and market
                      demands.
                    </div>
                  </div>
                </div>
                <div className="accordion-item border-0 mb-3 shadow-sm">
                  <h2 className="accordion-header" id="headingSeven">
                    <button
                      className="accordion-button collapsed"
                      type="button"
                      data-bs-toggle="collapse"
                      data-bs-target="#collapseSeven"
                      aria-expanded="false"
                      aria-controls="collapseSeven"
                    >
                      Can I customize the chatbot to match my brand?
                    </button>
                  </h2>
                  <div
                    id="collapseSeven"
                    className="accordion-collapse collapse"
                    aria-labelledby="headingSeven"
                    data-bs-parent="#pricingFaq"
                  >
                    <div className="accordion-body">
                      Absolutely! All plans include basic customization options like adding your
                      logo and brand colors. Higher-tier plans offer advanced customization
                      including custom chat flows, personalized greetings, and tailored responses
                      that match your brand voice. Our Enterprise plan includes a dedicated design
                      team to ensure your chatbot perfectly represents your brand identity.
                    </div>
                  </div>
                </div>
                <div className="accordion-item border-0 mb-3 shadow-sm">
                  <h2 className="accordion-header" id="headingEight">
                    <button
                      className="accordion-button collapsed"
                      type="button"
                      data-bs-toggle="collapse"
                      data-bs-target="#collapseEight"
                      aria-expanded="false"
                      aria-controls="collapseEight"
                    >
                      What kind of support do you provide?
                    </button>
                  </h2>
                  <div
                    id="collapseEight"
                    className="accordion-collapse collapse"
                    aria-labelledby="headingEight"
                    data-bs-parent="#pricingFaq"
                  >
                    <div className="accordion-body">
                      All plans include access to our comprehensive knowledge base and community
                      forum. The Base plan includes email support with a 48-hour response time. The
                      Popular plan adds priority email support (24-hour response) and chat support
                      during business hours. The Enterprise plan includes 24/7 priority support with
                      a dedicated account manager and phone support.
                    </div>
                  </div>
                </div>
                <div className="accordion-item border-0 mb-3 shadow-sm">
                  <h2 className="accordion-header" id="headingNine">
                    <button
                      className="accordion-button collapsed"
                      type="button"
                      data-bs-toggle="collapse"
                      data-bs-target="#collapseNine"
                      aria-expanded="false"
                      aria-controls="collapseNine"
                    >
                      When will the "Coming Soon" features be available?
                    </button>
                  </h2>
                  <div
                    id="collapseNine"
                    className="accordion-collapse collapse"
                    aria-labelledby="headingNine"
                    data-bs-parent="#pricingFaq"
                  >
                    <div className="accordion-body">
                      We're actively developing our upcoming features including Blue Tick Setup,
                      Autoreply & Follow-ups, Portfolio Management, Multinumber Campaign, and AI
                      Campaign Manager. These features are scheduled for release in the next 3-6
                      months. Customers on paid plans will get early access to beta versions of
                      these features before their official launch. Sign up for our newsletter to
                      stay updated on release dates.
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Pricing;
